import { trackError } from '@billy/track-error'
import { useCallback, useMemo } from 'react'
import { useQueries } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { FecThirdPartyCategory } from '../enums/fecThirdPartyCategory'
import { fetchFecThirdPartyMappings } from '../query-api'
import type { FecThirdPartyMapping } from '../types/fecThirdPartyMapping'
import { dedupeFecThirdPartyMappings } from '../utils/dedupeFecThirdPartyMappings'
import { toFecThirdPartyMappings } from '../utils/toFecThirdPartyMappings'
import { uniqueIds } from '../utils/uniqueIds'

const LISTED_CATEGORIES: ReadonlySet<FecThirdPartyCategory> = new Set([
  FecThirdPartyCategory.Create,
  FecThirdPartyCategory.Reuse,
  FecThirdPartyCategory.SimilarName,
])

interface FecThirdPartyMappingsQueryData {
  hasExistingContacts: boolean
  rows: FecThirdPartyMapping[]
}

export interface UseFetchFecThirdPartyMappingsResult {
  hasExistingContacts: boolean
  isError: boolean
  isLoading: boolean
  isRetrying: boolean
  retry: () => void
  thirdPartyMappings: FecThirdPartyMapping[]
}

export const useFetchFecThirdPartyMappings = (fecImportIds: string[]): UseFetchFecThirdPartyMappingsResult => {
  const { organization } = useUserOrganization()
  const organizationId = organization?.id || ''
  const uniqueFecImportIds = useMemo(() => uniqueIds(fecImportIds), [fecImportIds])

  const queries = useQueries(
    uniqueFecImportIds.map((fecImportId) => ({
      enabled: !!organizationId && !!fecImportId,
      queryFn: async (): Promise<FecThirdPartyMappingsQueryData> => {
        try {
          const response = await fetchFecThirdPartyMappings(organizationId, fecImportId)

          // Blocked lines are not listed here; they are handled in Resolve items (ADVP-601).
          const listedItems = response.items.filter((item) => item.category !== FecThirdPartyCategory.Blocked)

          const unexpectedCategories = new Set(
            listedItems.map(({ category }) => category).filter((category) => !LISTED_CATEGORIES.has(category)),
          )

          if (unexpectedCategories.size > 0) {
            trackError(
              new Error(
                `Unexpected FEC third-party categories for import ${fecImportId}: ${[...unexpectedCategories].join(', ')}`,
              ),
            )
          }

          if (typeof response.hasExistingContacts !== 'boolean') {
            trackError(new Error(`FEC third-party mappings for import ${fecImportId} have no hasExistingContacts flag`))
          }

          return {
            hasExistingContacts: response.hasExistingContacts === true,
            rows: toFecThirdPartyMappings(listedItems, fecImportId),
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)

          throw new Error(`FEC third-party mappings fetch failed for import ${fecImportId}: ${message}`)
        }
      },
      queryKey: [QueryKeys.FecThirdPartyMappings, organizationId, fecImportId],
    })),
  )

  const dataUpdatedAtKey = queries.map((query) => query.dataUpdatedAt).join('|')

  const thirdPartyMappings = useMemo(
    () => dedupeFecThirdPartyMappings(queries.flatMap((query) => query.data?.rows || [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by dataUpdatedAt snapshots
    [dataUpdatedAtKey],
  )

  const retry = useCallback(() => {
    for (const query of queries) {
      if (query.isError) {
        query.refetch()
      }
    }
  }, [queries])

  return {
    hasExistingContacts: queries.some((query) => query.data?.hasExistingContacts),
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading || query.isIdle),
    isRetrying: queries.some((query) => query.isError && query.isFetching),
    retry,
    thirdPartyMappings,
  }
}
